import { Component, DestroyRef, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SelectionModel } from '@angular/cdk/collections';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequiredObservationResultsTableComponent } from '../../shared/required-observation-results-table/required-observation-results-table.component';

export interface SelectedObsDialogData {
  selectedObservations: any[];
  editable?: boolean;
  serviceRequestIds?: string[];
  onSelectionChanged?: (selectedRows: any[]) => void;
}

@Component({
  selector: 'app-selected-obs',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTableModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    RequiredObservationResultsTableComponent
  ],
  templateUrl: './selected-obs.component.html',
  styleUrl: './selected-obs.component.scss'
})
export class SelectedObsComponent implements OnInit, OnChanges {
  @Input() selectedObservations: any[] = [];
  @Input() editable = true;
  @Input() serviceRequestIds?: string[];

  @Output() selectionChanged = new EventEmitter<any[]>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly data = inject(MAT_DIALOG_DATA, { optional: true }) as SelectedObsDialogData | null;
  dialogRef = inject(MatDialogRef<SelectedObsComponent>, { optional: true }) as MatDialogRef<SelectedObsComponent> | null;

  dataSource = new MatTableDataSource<any>([]);
  selection = new SelectionModel<any>(true, []);

  tableColumns: string[] = ['select', 'test', 'result', 'status', 'date'];

  constructor() {
    const data = this.data;
    if (!data) return;

    this.selectedObservations = data.selectedObservations ?? [];
    this.editable = data.editable ?? true;
    if (data.serviceRequestIds) this.serviceRequestIds = data.serviceRequestIds ?? [];
  }

  close(): void {
    this.dialogRef?.close({ selectedObservations: this.selectedObservations });
  }

  ngOnInit(): void {
    this.rebuildColumns();
    this.setRows(this.selectedObservations);

    // Listen to selection changes from row click and checkboxes
    this.selection.changed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const selected = [...this.selection.selected];
        this.selectionChanged.emit(selected);
        this.data?.onSelectionChanged?.(selected);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editable']) {
      this.rebuildColumns();
    }

    if (changes['selectedObservations']) {
      this.setRows(this.selectedObservations);
    }
  }

  private rebuildColumns(): void {
    this.tableColumns = ['select', 'test', 'result', 'status', 'date'];
    if (this.editable) this.tableColumns.push('actions');
  }

  private setRows(rows: any[]): void {
    const next = rows ?? [];
    this.dataSource.data = next;

    // Treat selection as "included" rows.
    this.selection.clear();
    if (next.length) this.selection.select(...next);
  }

  // --- Selection helpers (Angular Material table pattern)
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numRows > 0 && numSelected === numRows;
  }

  toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this.dataSource.data);
  }

  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row`;
  }

  // --- Delete helpers
  deleteRow(row: any): void {
    if (!this.editable) return;

    const next = (this.dataSource.data ?? []).filter(r => r !== row);
    this.selectedObservations = next;
    this.setRows(next);
  }

  deleteSelected(): void {
    if (!this.editable) return;
    const selected = new Set(this.selection.selected);
    if (selected.size === 0) return;

    const next = (this.dataSource.data ?? []).filter(r => !selected.has(r));
    this.selectedObservations = next;
    this.setRows(next);
  }

  // --- Safe display helpers (FHIR-ish Observations supported, but works with plain objects too)
  getTest(obs: any): string {
    return (
      obs?.code?.text ??
      obs?.code?.coding?.[0]?.display ??
      obs?.code?.coding?.[0]?.code ??
      obs?.test ??
      '—'
    );
  }

  getResult(obs: any): string {
    const vq = obs?.valueQuantity;
    if (vq?.value != null) {
      const unit = vq?.unit ?? vq?.code ?? '';
      return `${vq.value}${unit ? ' ' + unit : ''}`;
    }
    return (
      obs?.valueString ??
      obs?.valueCodeableConcept?.text ??
      obs?.valueCodeableConcept?.coding?.[0]?.display ??
      obs?.result ??
      '—'
    );
  }

  getStatus(obs: any): string {
    return obs?.status ?? obs?.interpretation?.[0]?.text ?? obs?.statusDisplay ?? '—';
  }

  getDate(obs: any): string {
    return obs?.effectiveDateTime ?? obs?.issued ?? obs?.date ?? '—';
  }

  private obsKey(obs: any): string {
    const id = obs?.id;
    if (id) return `id:${id}`;

    const coding = obs?.code?.coding?.[0];
    if (coding?.system || coding?.code) return `code:${coding?.system ?? ''}|${coding?.code ?? ''}`;

    const text = obs?.code?.text ?? obs?.test ?? '';
    const date = obs?.effectiveDateTime ?? obs?.issued ?? obs?.date ?? '';
    return `fallback:${text}|${date}`;
  }

  addMore(): void {
    const dialogRef = this.dialog.open(RequiredObservationResultsTableComponent, {
      width: '900px',
      maxHeight: '93vh',
      data: {
        serviceRequestIds: this.serviceRequestIds ?? [],
        forSelection: true,
        observationsToMatch: this.selectedObservations ?? []
      }
    });

    const selectionSub = dialogRef.componentInstance.selectionChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rows: any[]) => {
        const picked = (rows ?? [])
          .map(r => r?.observation)
          .filter(Boolean);

        const current = this.dataSource.data ?? [];
        const merged = new Map<string, any>();
        for (const o of current) merged.set(this.obsKey(o), o);
        for (const o of picked) merged.set(this.obsKey(o), o);

        const next = Array.from(merged.values());
        this.selectedObservations = next;
        this.setRows(next);
      });

    dialogRef.afterClosed().subscribe(() => selectionSub.unsubscribe());
  }
}
